import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnChanges,
    OnInit,
    QueryList,
    SimpleChanges,
    ViewChildren,
    ViewEncapsulation,
} from '@angular/core';
import { CustomerBillingInvoiceModel } from '../customer-billing-invoice.model';
import { CustomerBillingInvoiceDetailModel } from '../customer-billing-invoice-detail.model';
import { InvoiceService } from '../invoice.service';
import { isEqual } from '@fi-shared/utils';
import { CustomerBillingHelperService } from '@fi-app/customer-billing/customer-billing-helper.service';
import { getPriceWithSeprator } from '@fi-shared/utils/get-price-with-separator';
import { AutocompleteComponent } from '@fi-app/utilities/autocomplete/autocomplete.component';
import { AutocompleteSettingsModel } from '@fi-app/utilities/autocomplete/autocomplete-settings.model';
import { removeItem } from '@fi-shared/utils/remove-item';

@Component({
    selector: 'fi-customer-billing-invoice-edit-invoice',
    templateUrl: './customer-billing-invoice-edit-invoice.component.html',
    styleUrls: ['./customer-billing-invoice-edit-invoice.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-customer-billing-invoice-edit-invoice' },
    standalone: false,
})
export class CustomerBillingInvoiceEditInvoiceComponent implements OnInit, OnChanges {
    @Input() invoice: CustomerBillingInvoiceModel;
    @Input() billingCustomerId: number;
    @ViewChildren(AutocompleteComponent) autocompleteComponents: QueryList<AutocompleteComponent>;

    ActivationStartDateOpened = false;
    ActivationEndDateOpened = false;
    nonModulePriceArticles: CustomerBillingInvoiceDetailModel[] = [];
    autocompleteSettings: AutocompleteSettingsModel;

    constructor(
        private invoiceService: InvoiceService,
        private customerBillingHelperService: CustomerBillingHelperService,
    ) {}

    ngOnInit() {
        this.autocompleteSettings = new AutocompleteSettingsModel();
        this.autocompleteSettings.DataTextField = 'Description';
        this.autocompleteSettings.DataValueField = 'Description';
        this.getNonModulePriceArticles();
    }

    getNonModulePriceArticles() {
        if (!this.invoice.BillingCustomerId) return;

        this.invoiceService
            .GetNonModulePriceArticles(this.invoice.BillingCustomerId)
            .then((articles: CustomerBillingInvoiceDetailModel[]) => this.setupNonModulePriceArticles(articles));
    }

    ngOnChanges(changes: SimpleChanges): void {
        const change = changes.billingCustomerId;
        if (change && !change.firstChange) {
            const newBillingCustomerId = change.currentValue;

            if (!newBillingCustomerId) {
                this.setupNonModulePriceArticles();
                return;
            }

            this.invoiceService
                .GetNonModulePriceArticles(newBillingCustomerId)
                .then((articles: CustomerBillingInvoiceDetailModel[]) => this.setupNonModulePriceArticles(articles));
        }
    }

    triggerTab($event: KeyboardEvent) {
        if ($event.keyCode === 13) {
            $event.preventDefault();
            const tabs: Element[] = Array.from(document.getElementsByClassName('inv-tabstop'));
            const index: number = tabs.findIndex((element: Element) => isEqual(element, $event.currentTarget));
            if (index < tabs.length - 1) (tabs[index + 1] as HTMLElement).focus();
        }
    }

    // note: not used ?
    ActivationStartDateOpenedToggle() {
        this.ActivationStartDateOpened = !this.ActivationStartDateOpened;
    }

    // note: not used ?
    ActivationEndDateOpenedToggle() {
        this.ActivationEndDateOpened = !this.ActivationEndDateOpened;
    }

    addInvoiceRow() {
        this.invoice.InvoiceDetails.push(
            new CustomerBillingInvoiceDetailModel({
                InvoiceDetailType: CustomerBillingInvoiceDetailModel.InvoiceDetailTypeInvoiceRow,
            }),
        );
        setTimeout(() => this.focusDescriptionInput());
    }

    private focusDescriptionInput() {
        if (this.autocompleteComponents.length) {
            this.autocompleteComponents.last.focusInput();
        }
    }

    addInvoiceComment() {
        this.invoice.InvoiceDetails.push(
            new CustomerBillingInvoiceDetailModel({
                InvoiceDetailType: CustomerBillingInvoiceDetailModel.InvoiceDetailTypeComment,
            }),
        );
    }

    removeInvoiceRow(row: CustomerBillingInvoiceDetailModel) {
        removeItem(this.invoice.InvoiceDetails, row);
    }

    private setupNonModulePriceArticles(articles?: CustomerBillingInvoiceDetailModel[]) {
        if (!articles) {
            this.nonModulePriceArticles = [];
            return;
        }

        this.nonModulePriceArticles = articles.map((item: CustomerBillingInvoiceDetailModel) => {
            //item.Name = item.Description;
            //item.Id = item.Description;
            return item;
        });
    }

    getCurrencyName(): string {
        return this.customerBillingHelperService.getCurrencyName(this.invoice.CurrencyType);
    }

    getTotalAmount(invoiceDetail: CustomerBillingInvoiceDetailModel): string {
        return getPriceWithSeprator(this.customerBillingHelperService.getInvoiceDetailsTotalAmount(invoiceDetail));
    }

    getVATAmount(): string {
        return getPriceWithSeprator(
            this.customerBillingHelperService.getInvoiceVATAmount(this.invoice.InvoiceDetails, this.invoice.ChargeVAT),
        );
    }

    getTotalAmountExcVat(): string {
        return getPriceWithSeprator(
            this.customerBillingHelperService.getInvoiceTotalAmountExcVat(this.invoice.InvoiceDetails),
        );
    }

    getTotalAmountIncVat(): string {
        return getPriceWithSeprator(
            this.customerBillingHelperService.getInvoiceTotalAmountIncVat(
                this.invoice.InvoiceDetails,
                this.invoice.ChargeVAT,
            ),
        );
    }
}
